<template>
  <v-main>
    <v-container fluid class="sms-test-page pa-4 pa-md-6">
      <!-- Page header -->
      <v-row class="mb-4" align="center" dense>
        <v-col cols="12" md="8" class="d-flex align-center">
          <v-icon color="primary" size="36" class="mr-3">mdi-message-text-fast</v-icon>
          <div style="min-width: 0;">
            <h1 class="text-h5 font-weight-bold mb-1">SMS Template Tester</h1>
            <p class="text-body-2 grey--text mb-0">
              Send any registered DLT template to a real or custom phone number for verification.
            </p>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right">
          <v-chip color="primary" outlined small>
            <v-icon left small>mdi-cellphone-message</v-icon>
            Provider: MSG91
          </v-chip>
        </v-col>
      </v-row>

    <v-row>
      <!-- LEFT: Template picker -->
      <v-col cols="12" md="4">
        <v-card class="template-list-card" elevation="2">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
            Templates
            <v-spacer />
            <v-chip x-small color="primary" text-color="white">{{ templates.length }}</v-chip>
          </v-card-title>

          <v-text-field
            v-model="templateSearch"
            placeholder="Search templates..."
            prepend-inner-icon="mdi-magnify"
            dense outlined hide-details clearable
            class="mx-4 mb-3"
          />

          <v-divider />

          <div class="template-scroll">
            <v-list dense v-if="filteredTemplates.length" class="py-0">
              <v-list-item
                v-for="tpl in filteredTemplates"
                :key="tpl.key"
                @click="selectTemplate(tpl)"
                :class="{ 'tpl-active': selected && selected.key === tpl.key }"
                class="tpl-item"
              >
                <v-avatar :color="colorFor(tpl.key)" size="28" class="mr-3">
                  <v-icon x-small dark>{{ iconFor(tpl.key) }}</v-icon>
                </v-avatar>
                <v-list-item-content class="py-2">
                  <v-list-item-title class="font-weight-medium text-truncate">{{ tpl.name }}</v-list-item-title>
                  <v-list-item-subtitle class="caption text-truncate">{{ tpl.description }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-icon v-if="selected && selected.key === tpl.key" color="primary" small>mdi-check-circle</v-icon>
              </v-list-item>
            </v-list>
            <div v-else class="text-center pa-6 grey--text">
              <v-icon large color="grey lighten-1">mdi-magnify-close</v-icon>
              <div class="mt-2 caption">No templates match your search.</div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- RIGHT: Form + Preview -->
      <v-col cols="12" md="8">
        <v-card v-if="!selected" class="empty-state-card pa-12 text-center" elevation="2">
          <v-icon size="80" color="grey lighten-2">mdi-message-text-outline</v-icon>
          <h2 class="text-h6 mt-4 grey--text text--darken-1">Pick a template to test</h2>
          <p class="grey--text">Choose any SMS template from the list to configure and send.</p>
        </v-card>

        <template v-else>
          <!-- Recipient -->
          <v-card class="mb-4" elevation="2">
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-account-arrow-right</v-icon>
              Recipient
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-btn-toggle v-model="recipientMode" mandatory dense color="primary" class="mb-4">
                <v-btn value="user" small>
                  <v-icon left small>mdi-account-search</v-icon> Pick a User
                </v-btn>
                <v-btn value="custom" small>
                  <v-icon left small>mdi-cellphone-key</v-icon> Custom Phone
                </v-btn>
              </v-btn-toggle>

              <!-- Pick user -->
              <v-autocomplete
                v-if="recipientMode === 'user'"
                v-model="selectedUser"
                :items="userResults"
                :loading="searchingUsers"
                :search-input.sync="userQuery"
                :item-text="userDisplay"
                item-value="id"
                placeholder="Search by name, phone, or email..."
                prepend-inner-icon="mdi-account-search"
                outlined dense return-object hide-no-data clearable
                no-filter cache-items
              >
                <template v-slot:item="{ item }">
                  <v-list-item-avatar>
                    <v-avatar color="primary" size="36">
                      <span class="white--text caption">{{ initials(item) }}</span>
                    </v-avatar>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ fullName(item) }}</v-list-item-title>
                    <v-list-item-subtitle class="caption">
                      <v-icon x-small>mdi-phone</v-icon> {{ item.phone || 'No phone' }}
                      <span v-if="item.email" class="ml-2">
                        <v-icon x-small>mdi-email</v-icon> {{ item.email }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>

              <!-- Custom phone -->
              <v-text-field
                v-else
                v-model="customPhone"
                label="Phone number"
                placeholder="10-digit mobile number"
                prepend-inner-icon="mdi-cellphone"
                outlined dense
                :rules="phoneRules"
                maxlength="10"
                hint="No need to be in the database — any 10-digit Indian mobile works."
                persistent-hint
              />

              <v-alert
                v-if="resolvedPhone"
                color="success" outlined dense class="mt-3 mb-0"
                icon="mdi-check-circle"
              >
                SMS will be sent to <strong>{{ resolvedPhone }}</strong>
                <span v-if="recipientMode === 'user' && selectedUser">
                  · <strong>{{ fullName(selectedUser) }}</strong>
                </span>
              </v-alert>
            </v-card-text>
          </v-card>

          <!-- Variables -->
          <v-card v-if="selected.vars.length" class="mb-4" elevation="2">
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-variable</v-icon>
              Template Variables
              <v-spacer />
              <v-btn text x-small color="primary" @click="fillDefaults">
                <v-icon left x-small>mdi-restore</v-icon> Reset to defaults
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-row dense>
                <v-col
                  v-for="varDef in selected.vars"
                  :key="varDef.key"
                  cols="12" :sm="selected.vars.length > 1 ? 6 : 12"
                >
                  <v-text-field
                    v-model="varValues[varDef.key]"
                    :label="varDef.label"
                    :placeholder="varDef.placeholder"
                    outlined dense
                    persistent-placeholder
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-alert
            v-else
            color="info" outlined dense class="mb-4"
            icon="mdi-information-outline"
          >
            This template has no variables — it's a static message.
          </v-alert>

          <!-- Preview + Send -->
          <v-card elevation="2">
            <v-card-title class="subtitle-1 font-weight-bold">
              <v-icon left color="primary">mdi-eye-outline</v-icon>
              Live Preview
              <v-spacer />
              <v-chip x-small color="grey lighten-3">
                {{ previewMessage.length }} chars
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="sms-preview">
                <div class="sms-bubble">
                  <div class="sms-sender">
                    <v-icon small class="mr-1">mdi-cellphone-message</v-icon>
                    HTTAN
                  </div>
                  <div class="sms-body">{{ previewMessage }}</div>
                  <div class="sms-time">Now</div>
                </div>
              </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="px-4 py-3">
              <v-btn text @click="selected = null">
                <v-icon left>mdi-arrow-left</v-icon> Back
              </v-btn>
              <v-spacer />
              <v-btn
                color="primary" large
                :loading="sending"
                :disabled="!canSend"
                @click="sendSms"
              >
                <v-icon left>mdi-send</v-icon>
                Send Test SMS
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Result -->
          <v-card
            v-if="lastResult"
            class="mt-4"
            :color="resultColor"
            elevation="1"
          >
            <v-card-text>
              <div class="d-flex align-start">
                  <v-icon :color="resultIconColor" size="32" class="mr-3">
                  {{ resultIcon }}
                </v-icon>
                <div style="flex: 1;">
                  <div class="subtitle-1 font-weight-bold">{{ resultHeading }}</div>
                  <div class="caption grey--text text--darken-1">{{ lastResult.message }}</div>
                  <div v-if="lastResult.request_id" class="caption mt-1 grey--text text--darken-1">
                    Request ID: <code>{{ lastResult.request_id }}</code>
                  </div>
                  <v-expansion-panels flat class="mt-3" v-if="lastResult.provider_reply || lastResult.delivery_raw">
                    <v-expansion-panel style="background: transparent;">
                      <v-expansion-panel-header class="caption px-0">
                        Provider response details
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <pre class="provider-reply">{{ JSON.stringify({
                          send: lastResult.provider_reply,
                          delivery: lastResult.delivery_raw,
                        }, null, 2) }}</pre>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
                <v-btn icon small @click="closeResult">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-col>
    </v-row>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" top right>
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from '@/axios';

let userSearchTimer = null;

export default {
  name: 'SmsTestPage',
  data() {
    return {
      templates: [],
      templateSearch: '',
      selected: null,
      varValues: {},

      recipientMode: 'user',
      customPhone: '',
      selectedUser: null,
      userQuery: '',
      userResults: [],
      searchingUsers: false,

      sending: false,
      lastResult: null,
      pollTimer: null,

      snackbar: { show: false, text: '', color: 'info', icon: 'mdi-information' },

      phoneRules: [
        v => !!v || 'Phone number is required',
        v => /^[0-9]{10}$/.test(v || '') || 'Must be exactly 10 digits',
      ],
    };
  },
  computed: {
    filteredTemplates() {
      const q = (this.templateSearch || '').toLowerCase();
      if (!q) return this.templates;
      return this.templates.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.key.toLowerCase().includes(q)
      );
    },
    resolvedPhone() {
      if (this.recipientMode === 'custom') {
        return /^[0-9]{10}$/.test(this.customPhone) ? this.customPhone : '';
      }
      // Strip non-digits and country code from the picked user's stored phone.
      const digits = String(this.selectedUser?.phone || '').replace(/\D/g, '');
      const last10 = digits.slice(-10);
      return /^\d{10}$/.test(last10) ? last10 : '';
    },
    previewMessage() {
      if (!this.selected) return '';
      let msg = this.selected.preview;
      this.selected.vars.forEach(v => {
        const val = this.varValues[v.key] !== undefined && this.varValues[v.key] !== ''
          ? this.varValues[v.key]
          : (v.default || `{${v.key}}`);
        msg = msg.split(`{${v.key}}`).join(val);
      });
      return msg;
    },
    canSend() {
      return !!this.selected && !!this.resolvedPhone && !this.sending;
    },
    resultColor() {
      if (!this.lastResult) return '';
      const s = this.lastResult.deliveryStatus;
      // failed = red; anything accepted by MSG91 = green shades
      if (s === 'failed')    return 'red lighten-5';
      if (!this.lastResult.success && s !== 'delivered') return 'red lighten-5';
      return 'green lighten-5';
    },
    resultIconColor() {
      const s = this.lastResult?.deliveryStatus;
      if (s === 'failed')    return 'error';
      if (!this.lastResult?.success) return 'error';
      if (s === 'delivered') return 'success';
      return 'success';
    },
    resultIcon() {
      const s = this.lastResult?.deliveryStatus;
      if (s === 'failed')    return 'mdi-alert-circle';
      if (!this.lastResult?.success) return 'mdi-alert-circle';
      if (s === 'delivered') return 'mdi-check-circle';
      return 'mdi-check-circle';
    },
    resultHeading() {
      const s = this.lastResult?.deliveryStatus;
      if (!this.lastResult?.success) return 'SMS Failed';
      if (s === 'delivered')   return 'SMS Delivered to User ✓';
      if (s === 'failed')      return 'SMS Delivery Failed';
      // sent / pending / unavailable / timeout — MSG91 accepted it
      return 'SMS Sent Successfully';
    },
  },
  watch: {
    userQuery(val) {
      if (userSearchTimer) clearTimeout(userSearchTimer);
      if (!val || val.length < 2) return;
      userSearchTimer = setTimeout(() => this.searchUsers(val), 300);
    },
  },
  mounted() {
    this.loadTemplates();
  },
  beforeDestroy() {
    this.cancelPolling();
  },
  methods: {
    async loadTemplates() {
      try {
        const res = await axios.get('/sms/templates');
        this.templates = res.data?.data || [];
      } catch (e) {
        this.toast('Failed to load templates', 'error', 'mdi-alert-circle');
      }
    },
    selectTemplate(tpl) {
      this.selected = tpl;
      this.varValues = {};
      tpl.vars.forEach(v => { this.$set(this.varValues, v.key, v.default || ''); });
      this.lastResult = null;
    },
    fillDefaults() {
      if (!this.selected) return;
      this.selected.vars.forEach(v => { this.$set(this.varValues, v.key, v.default || ''); });
    },
    async searchUsers(q) {
      this.searchingUsers = true;
      try {
        const res = await axios.get('/users', {
          params: { search_keyword: q, rowsPerPage: 30, page: 1 },
        });
        const raw  = res.data?.data || res.data?.users || [];
        const list = Array.isArray(raw) ? raw : (raw.data || []);
        // Only users who actually have a 10-digit phone — SMS test needs one.
        this.userResults = list.filter(u => /^\d{10}$/.test(String(u.phone || '').replace(/\D/g, '').slice(-10)));
      } catch (e) {
        this.userResults = [];
      } finally {
        this.searchingUsers = false;
      }
    },
    async sendSms() {
      if (!this.canSend) return;
      this.cancelPolling();
      this.sending = true;
      this.lastResult = null;
      try {
        const res = await axios.post('/sms/send-test', {
          template: this.selected.key,
          phone: this.resolvedPhone,
          vars: this.varValues,
        });

        const data = res.data || {};
        this.lastResult = {
          ...data,
          deliveryStatus: data.success ? 'pending' : 'failed',
        };

        if (!data.success) {
          this.toast(data.message || 'SMS failed at provider', 'error', 'mdi-alert-circle');
          return;
        }

        // MSG91 accepted with TRAI DLT IDs = SMS will be delivered to the carrier.
        // Show green immediately; try one delivery poll after a short delay to
        // upgrade to "carrier confirmed" if the webhook cache already has the DLR.
        this.toast('SMS sent successfully', 'success', 'mdi-check-circle');
        if (data.request_id) {
          this.pollDelivery(data.request_id);
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || 'Network error';
        this.lastResult = {
          success: false,
          message: msg,
          deliveryStatus: 'failed',
          provider_reply: e.response?.data,
        };
        this.toast(msg, 'error', 'mdi-alert-circle');
      } finally {
        this.sending = false;
      }
    },
    pollDelivery(requestId) {
      const startedAt = Date.now();
      const TIMEOUT_MS  = 45000;  // give MSG91 up to 45s to deliver
      const INTERVAL_MS = 3000;

      const tick = async () => {
        if (!this.lastResult || this.lastResult.request_id !== requestId) return;

        try {
          const res = await axios.get(`/sms/delivery-status/${requestId}`);
          const data = res.data || {};
          this.lastResult = {
            ...this.lastResult,
            message: data.message,
            delivery_raw: data.raw,
            deliveryStatus: data.status,
          };

          if (data.delivered) {
            this.toast('SMS delivered to the user', 'success', 'mdi-check-circle');
            return;
          }
          if (data.failed) {
            this.toast('SMS delivery failed at carrier', 'error', 'mdi-alert-circle');
            return;
          }
          // Provider can't confirm delivery via polling (v5 logs = 401 unauthorized).
          // SMS is still sent — just stop polling silently, keep green state.
          if (data.status === 'unavailable' || data.status === 'unknown') {
            return;
          }
        } catch (_) {
          // Ignore transient errors and keep polling.
        }

        if (Date.now() - startedAt > TIMEOUT_MS) {
          // Polling timed out — but the SMS was accepted by MSG91 so it's sent.
          // Keep the green "SMS Sent" state, just stop polling.
          return;
        }

        this.pollTimer = setTimeout(tick, INTERVAL_MS);
      };

      tick();
    },
    cancelPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    closeResult() {
      this.cancelPolling();
      this.lastResult = null;
    },
    toast(text, color, icon) {
      this.snackbar = { show: true, text, color, icon };
    },
    fullName(u) {
      if (!u) return '';
      return [u.first_name, u.middle_name, u.last_name].filter(Boolean).join(' ').trim() || u.user_name || 'Unnamed';
    },
    initials(u) {
      const name = this.fullName(u);
      return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
    },
    userDisplay(u) {
      const name = this.fullName(u);
      return `${name}${u.phone ? ' · ' + u.phone : ''}`;
    },
    iconFor(key) {
      const map = {
        otp: 'mdi-shield-key',
        order_confirmation: 'mdi-cart-check',
        order_cancelled: 'mdi-cart-remove',
        out_for_delivery: 'mdi-truck-delivery',
        order_delivered: 'mdi-package-variant-closed-check',
        refund_initiated: 'mdi-cash-refund',
        appointment_reminder_user: 'mdi-bell-ring',
        appointment_reminder_doctor: 'mdi-bell-ring-outline',
        appointment_rescheduled_user: 'mdi-calendar-edit',
        appointment_rescheduled_doctor: 'mdi-calendar-edit-outline',
        appointment_cancelled_user: 'mdi-calendar-remove',
        appointment_cancelled_doctor: 'mdi-calendar-remove-outline',
      };
      return map[key] || 'mdi-message-text';
    },
    colorFor(key) {
      if (key.startsWith('order_')) return 'orange';
      if (key.startsWith('appointment_')) return 'purple';
      if (key === 'refund_initiated') return 'teal';
      if (key === 'otp') return 'indigo';
      if (key === 'out_for_delivery') return 'amber darken-2';
      return 'primary';
    },
  },
};
</script>

<style scoped>
.sms-test-page {
  max-width: 1600px;
  margin: 0 auto;
}

.template-list-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.template-scroll {
  max-height: 560px;
  overflow-y: auto;
}

@media (min-width: 960px) {
  .template-scroll {
    max-height: calc(100vh - 280px);
  }
}

.tpl-item {
  cursor: pointer;
  transition: background-color 0.18s ease;
  border-left: 3px solid transparent;
}
.tpl-item:hover { background-color: rgba(25, 118, 210, 0.05); }
.tpl-active {
  background-color: rgba(25, 118, 210, 0.08) !important;
  border-left-color: #1976d2 !important;
}

.empty-state-card {
  border-radius: 12px !important;
  background: white;
}

.sms-preview {
  background: linear-gradient(135deg, #e8f0fe 0%, #f5f7fa 100%);
  padding: 24px;
  border-radius: 12px;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sms-bubble {
  background: white;
  border-radius: 18px 18px 18px 4px;
  padding: 14px 18px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: relative;
}

.sms-sender {
  font-size: 0.72rem;
  font-weight: 600;
  color: #1976d2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.sms-body {
  font-size: 0.95rem;
  line-height: 1.55;
  color: #2c3e50;
  white-space: pre-wrap;
  word-break: break-word;
}

.sms-time {
  font-size: 0.7rem;
  color: #95a5a6;
  text-align: right;
  margin-top: 6px;
}

.provider-reply {
  font-size: 0.78rem;
  background: rgba(0, 0, 0, 0.04);
  padding: 12px;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Menlo', 'Monaco', monospace;
  color: #2c3e50;
}

::v-deep .v-card__title.subtitle-1 {
  font-size: 0.95rem !important;
}
</style>
